import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { Checkbox } from 'primereact/checkbox';
import { Menu } from 'primereact/menu';
import { BlockUI } from 'primereact/blockui';
import PageHeader, { EmptyState, tableEmpty } from '../common/PageHeader';
import DateInput from '../common/DateInput';
import TableSearch, { filtriraj, useFitRows, usePage, PAGINATOR } from '../common/TableSearch';
import { driversApi, kazneApi, exportApi } from '../../api/services';
import { blobError, download } from '../Reports/reportUtils';
import { useAlerts } from '../../custom/Alerts';
import { addDays, fmtDate, fmtDateTime, startOfToday } from '../../utils/dates';
import { driverLabel } from '../../utils/activities';
import { exportXlsx, stampaVremena } from '../../utils/excel';
import { izracunajPrekrsaje, fmtTrajanje } from './engine/motor';
import { REZIMI, TIPOVI, NIVOI_LISTA, KAZNE_VAN_OBRACUNA, nazivRezima, fmtRsd } from './engine/propisi';
import { psihoKomentari } from './psihoKomentari';
import { JEZICI, vozaciZaOpomenu, generisiOpomeneVozacima } from './opomenaVozacu';

const TYPE_BY_ID = { 1: 'break/rest', 3: 'driving', 4: 'work', 5: 'availability', 6: 'unknown' };
const NIVO_KLASA = { 1: 'danger', 2: 'danger', 3: 'warn', 4: 'neutral' };
/* propisi su po vozaču (dbo.Driver.Turistika): Turistika -> AETR + R Srbije međugradski, inače R Srbije gradski (engine/motor rezimiVozaca) */
const OPIS_PROPISA = 'Propisi po vozaču: Turistika — AETR i R Srbije međugradski; ostali — R Srbije gradski';
/* obračun koristi i dane posle perioda (pre perioda: 28 dana, procedura) — prikazuju se samo kazne koje se preklapaju sa periodom */
const POSLE_DANA = 14;

/* ── Prekršaji ────────────────────────────────────────────────────────────── */
/* sa uvezenih kartica: udvojena posada i unete zemlje početka radnog dana (za vozače bez uvezene kartice prazno) */
const kartica = async (drivers, from, to) => {
    const body = { driverIds: drivers.map((x) => x.id), startDate: addDays(from, -30), endDate: addDays(to, POSLE_DANA) };
    const [posada, mesta] = await Promise.all([kazneApi.posada(body), kazneApi.mesta(body)]);
    return { posada, mesta };
};

function Prekrsaji({ skriveni, active }) {
    const alerts = useAlerts();
    const companies = useSelector((s) => s.companies.list);
    const [companyIds, setCompanyIds] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [driverIds, setDriverIds] = useState([]);
    const [from, setFrom] = useState(addDays(startOfToday(), -30));
    const [to, setTo] = useState(startOfToday());
    const [busy, setBusy] = useState(false);
    const [rezultat, setRezultat] = useState(null); // { list, period, vozaca }
    const [filterTip, setFilterTip] = useState(null);

    /* vozači izabranih kompanija (bez izbora kompanije — svi) */
    useEffect(() => {
        driversApi.all({ companyIds }).then((r) => {
            setDrivers(r.drivers);
            setDriverIds((ids) => ids.filter((id) => r.drivers.some((d) => d.id === id)));
        }).catch(() => {});
    }, [companyIds]);

    const valid = from && to && from <= to && (companyIds.length || driverIds.length);

    /* psihofizičko stanje: dnevna evidencija (X = radni dan vozača) za svaku izabranu kompaniju i period — isti format kao na produkciji;
       kazne po propisu vozača (svi vozači kompanije) se upisuju kao komentari ("Turistika: ..." / "Gradski: ..."), odmori zeleno */
    const [psiho, setPsiho] = useState(false);
    /* komentari u Excelu stalno otvoreni (inače na prelaz mišem) — pamti se u pregledaču */
    const [otvoreni, setOtvoreni] = useState(() => { try { return localStorage.getItem('psihoKomentariOtvoreni') === '1'; } catch { return false; } });
    const promeniOtvoreni = (v) => { setOtvoreni(v); try { localStorage.setItem('psihoKomentariOtvoreni', v ? '1' : '0'); } catch { /* bez pamćenja */ } };
    const stampajPsiho = async () => {
        setPsiho(true);
        for (const id of companyIds) {
            const firma = (companies || []).find((c) => c.id === id);
            const naziv = firma ? firma.name : 'Bez kompanije';
            try {
                const data = await kazneApi.data({ companyIds: [id], driverIds: [], startDate: from, endDate: addDays(to, POSLE_DANA) }); // eslint-disable-line no-await-in-loop
                const activities = data.activities.map((a) => ({ driverId: a.d, start: a.s, end: a.e, type: TYPE_BY_ID[a.t], note: a.n }));
                const outs = data.outs.map((o) => ({ driverId: o.d, entryDate: o.s, type: o.t }));
                const { posada, mesta } = await kartica(data.drivers, from, to); // eslint-disable-line no-await-in-loop
                const prekrsaji = izracunajPrekrsaje({ drivers: data.drivers, activities, outs, from, to, skriveni, tolerancija: true, posada, mesta });
                const komentari = psihoKomentari(prekrsaji, data.drivers, to);
                const res = await exportApi.psychostate({ companyId: id, startDate: from, endDate: to, komentari, komentariOtvoreni: otvoreni }); // eslint-disable-line no-await-in-loop
                download(res, `Izvestaj - ${naziv} - ${fmtDate(new Date())}.xlsx`);
            } catch (e) { alerts.error(`${naziv}: ${await blobError(e)}`, 'Psihofizičko'); } // eslint-disable-line no-await-in-loop
        }
        setPsiho(false);
    };

    const izracunaj = async () => {
        setBusy(true);
        try {
            const data = await kazneApi.data({ companyIds, driverIds, startDate: from, endDate: addDays(to, POSLE_DANA) });
            const activities = data.activities.map((a) => ({ driverId: a.d, start: a.s, end: a.e, type: TYPE_BY_ID[a.t], note: a.n }));
            const outs = data.outs.map((o) => ({ driverId: o.d, entryDate: o.s, type: o.t }));
            const { posada, mesta } = await kartica(data.drivers, from, to);
            const list = izracunajPrekrsaje({ drivers: data.drivers, activities, outs, from, to, skriveni, posada, mesta })
                .map((f, i) => ({ ...f, key: i }));
            setRezultat({ list, period: [from, to], vozaca: data.drivers.length });
            setFilterTip(null);
        } catch { /* toast */ }
        setBusy(false);
    };

    /* tabela staje na ekran: broj redova po strani prema visini ekrana, ostalo ide na sledeće strane */
    const tableRef = useRef(null);
    const poStrani = useFitRows(tableRef, active, { rezerva: 110, dep: rezultat });
    /* pretraga sužava prikaz — Excel, opomena i zbirovi rade nad onim što se vidi (kao filter tipa) */
    const [pretraga, setPretraga] = useState('');
    const [first, setFirst] = usePage(rezultat ? rezultat.list.length : 0, poStrani, `${filterTip}|${pretraga}|${rezultat ? rezultat.list.length : 0}`);

    const prikaz = useMemo(() => (rezultat ? filtriraj(rezultat.list.filter((f) => !filterTip || f.tip === filterTip), pretraga, (f) => [
        f.vozac, f.kompanija, fmtDateTime(f.vreme), nazivRezima(f.rezim), f.tipNaziv, f.opis, f.nivo ? `Nivo ${f.nivo}` : 'bez kazne', f.clanTacka,
        f.kazna && fmtRsd(f.kazna.pravno)]) : []), [rezultat, filterTip, pretraga]);
    const zbir = useMemo(() => {
        const z = { 1: 0, 2: 0, 3: 0, 4: 0, bez: 0, pravno: 0 };
        prikaz.forEach((f) => { if (f.nivo) { z[f.nivo] += 1; z.pravno += f.kazna ? f.kazna.pravno : 0; } else z.bez += 1; });
        return z;
    }, [prikaz]);
    const tipoviURezultatu = useMemo(() => {
        if (!rezultat) return [];
        const k = [...new Set(rezultat.list.map((f) => f.tip))];
        return TIPOVI.filter((t) => k.includes(t.kljuc)).map((t) => ({ label: `${nazivRezima(t.rezim)} — ${t.naziv}`, value: t.kljuc }));
    }, [rezultat]);

    const izvoz = () => exportXlsx({
        fileName: `Kazne - ${fmtDate(rezultat.period[0])} - ${fmtDate(rezultat.period[1])} - ${stampaVremena()}.xlsx`,
        sheetName: 'Prekršaji',
        title: 'Prekršaji i kazne',
        subtitle: `Period ${fmtDate(rezultat.period[0])} – ${fmtDate(rezultat.period[1])}; ${OPIS_PROPISA}. `
            + 'Iznosi: Zakon o radnom vremenu posade vozila u drumskom prevozu i tahografima (Sl. glasnik RS 96/15, 95/18, 96/25).',
        columns: [
            { header: 'Vozač', key: 'vozac' }, { header: 'Kompanija', key: 'kompanija' },
            { header: 'Vreme', value: (f) => f.vreme }, { header: 'Režim', value: (f) => nazivRezima(f.rezim) },
            { header: 'Prekršaj', key: 'tipNaziv', width: 40, wrap: true }, { header: 'Opis', key: 'opis', width: 80, wrap: true },
            { header: 'Nivo', value: (f) => f.nivo || '' }, { header: 'Član', value: (f) => f.clanTacka || '' },
            { header: 'Pravno lice (RSD)', value: (f) => (f.kazna ? f.kazna.pravno : ''), numFmt: '#,##0' },
            { header: 'Odgovorno lice (RSD)', value: (f) => (f.kazna ? f.kazna.odgovorno : ''), numFmt: '#,##0' },
            { header: 'Vozač (RSD)', value: (f) => (f.kazna ? f.kazna.vozac : ''), numFmt: '#,##0' },
            { header: 'Napomena', value: (f) => f.napomena || '', width: 40, wrap: true },
        ],
        rows: prikaz,
    }).catch(() => alerts.error('Izvoz u Excel nije uspeo'));

    /* Opomena vozaču (Word): vozači sa kažnjivim prekršajima u trenutnom prikazu, na izabranom jeziku */
    const jezikMeni = useRef(null);
    const [opomena, setOpomena] = useState(false);
    const zaOpomenu = useMemo(() => vozaciZaOpomenu(prikaz, drivers), [prikaz, drivers]);
    const opomeni = async (jezik) => {
        setOpomena(true);
        try {
            await generisiOpomeneVozacima(zaOpomenu, jezik, { period: rezultat.period, nazivRezima });
            alerts.success(`Opomena je napravljena za ${zaOpomenu.length} vozača`);
        } catch { alerts.error('Pravljenje opomene nije uspelo'); }
        setOpomena(false);
    };

    const nivoBody = (f) => (f.nivo
        ? <span className={`pill ${NIVO_KLASA[f.nivo]}`}>Nivo {f.nivo}</span>
        : <span className="pill neutral" title={f.napomena || ''}>bez kazne</span>);
    const kaznaBody = (f) => (f.kazna ? (
        <div className="fine-cell">
            <b>{fmtRsd(f.kazna.pravno)}</b>
            <small>odg. lice {fmtRsd(f.kazna.odgovorno)} · vozač {fmtRsd(f.kazna.vozac)}</small>
        </div>
    ) : <small className="muted">{f.napomena || '—'}</small>);

    return (
        <BlockUI blocked={busy} template={<i className="pi pi-spin pi-spinner" style={{ fontSize: '2.5rem' }} aria-label="Učitavanje" />}>
            <div className="panel">
                <div className="filters">
                    <div className="field" style={{ flexBasis: '15rem' }}>
                        <label htmlFor="kz-co">Kompanija</label>
                        <MultiSelect inputId="kz-co" value={companyIds} options={companies} optionLabel="name" optionValue="id" filter
                            placeholder="Izaberite..." maxSelectedLabels={1} selectedItemsLabel="{0} izabrano" onChange={(e) => setCompanyIds(e.value)} />
                    </div>
                    <div className="field grow">
                        <label htmlFor="kz-dr">Vozači</label>
                        <MultiSelect inputId="kz-dr" value={driverIds} options={drivers.map((d) => ({ label: driverLabel(d), value: d.id }))} filter
                            placeholder={companyIds.length ? 'Svi vozači izabranih kompanija' : 'Izaberite vozače ili kompaniju'}
                            maxSelectedLabels={2} selectedItemsLabel="{0} vozača" onChange={(e) => setDriverIds(e.value)}
                            virtualScrollerOptions={drivers.length > 20 ? { itemSize: 40 } : undefined} />
                    </div>
                    <div className="field date"><label htmlFor="kz-from">Od</label>
                        <DateInput inputId="kz-from" value={from} onChange={(e) => setFrom(e.value)} dateFormat="dd.mm.yy" showIcon /></div>
                    <div className="field date"><label htmlFor="kz-to">Do</label>
                        <DateInput inputId="kz-to" value={to} onChange={(e) => setTo(e.value)} dateFormat="dd.mm.yy" showIcon /></div>
                    <div className="filters-actions">
                        <label className="check" htmlFor="kz-kom" title="U Excelu psihofizičkog komentari kazni stoje stalno otvoreni (inače se vide kad se miš postavi na ćeliju)">
                            <Checkbox inputId="kz-kom" checked={otvoreni} onChange={(e) => promeniOtvoreni(e.checked)} /> Komentari otvoreni
                        </label>
                        <Button label="Psihofizičko" icon="pi pi-file-excel" outlined onClick={stampajPsiho} loading={psiho}
                            disabled={!companyIds.length || !from || !to || from > to || busy}
                            tooltip="Dnevna evidencija o psihofizičkom stanju vozača (Excel) za izabrane kompanije i period, sa kaznama po propisu vozača kao komentarima" tooltipOptions={{ position: 'top', showOnDisabled: true }} />
                        <Button label="Izračunaj" icon="pi pi-calculator" onClick={izracunaj} disabled={!valid || busy} />
                    </div>
                </div>
                <p className="panel-note"><i className="pi pi-info-circle" aria-hidden="true" />
                    {OPIS_PROPISA} (oznaka Turistika u podacima vozača).
                    Obračun koristi i 28 dana pre i 14 dana posle perioda (nedeljni odmor, dve nedelje vožnje); prikazuju se kazne koje se preklapaju sa periodom.
                    Gradski propis važi za gradski i prigradski linijski prevoz i međumesni do 50 km na teritoriji RS.</p>
            </div>

            {!rezultat && (
                <div className="panel">
                    <EmptyState icon="pi pi-shield" title="Izaberite vozače i period"
                        text="Izaberite kompaniju (ili pojedinačne vozače) i period, pa kliknite Izračunaj." />
                </div>
            )}

            {rezultat && (
                <section className="panel" aria-labelledby="kz-res">
                    <div className="panel-head">
                        <div>
                            <h3 className="panel-title" id="kz-res">Prekršaji ({prikaz.length})</h3>
                            <p className="panel-sub">{rezultat.vozaca} vozača · {fmtDate(rezultat.period[0])} – {fmtDate(rezultat.period[1])} · propisi po vozaču (Turistika / Gradski)</p>
                        </div>
                        <div className="page-actions">
                            <Dropdown value={filterTip} options={tipoviURezultatu} onChange={(e) => setFilterTip(e.value)} showClear
                                placeholder="Svi tipovi prekršaja" style={{ minWidth: '18rem' }} aria-label="Filter po tipu prekršaja" />
                            <Button label="Excel" icon="pi pi-file-excel" outlined severity="secondary" onClick={izvoz} disabled={!prikaz.length} />
                            <Menu popup ref={jezikMeni} id="kz-opomena-jezik" model={JEZICI.map((j) => ({ label: j.naziv, command: () => opomeni(j.kljuc) }))} />
                            <Button label="Opomena vozaču" icon="pi pi-file-word" onClick={(e) => jezikMeni.current.toggle(e)} loading={opomena}
                                disabled={!zaOpomenu.length || opomena} aria-haspopup aria-controls="kz-opomena-jezik"
                                tooltip={zaOpomenu.length ? `Word opomena za ${zaOpomenu.length} vozača — kažnjivi prekršaji u trenutnom prikazu; izaberite jezik` : 'Nema kažnjivih prekršaja u trenutnom prikazu'}
                                tooltipOptions={{ position: 'top', showOnDisabled: true }} />
                        </div>
                    </div>
                    <div className="stat-row">
                        {[1, 2, 3, 4].map((n) => (
                            <div key={n} className="stat"><span className="stat-label">Nivo {n}</span><span className="stat-value">{zbir[n]}</span></div>
                        ))}
                        <div className="stat"><span className="stat-label">Bez kazne</span><span className="stat-value">{zbir.bez}</span></div>
                        <div className="stat wide"><span className="stat-label">Ukupno, pravno lice</span><span className="stat-value">{fmtRsd(zbir.pravno)}</span></div>
                    </div>
                    <TableSearch value={pretraga} onChange={setPretraga} placeholder="Pretraga (vozač, kompanija, prekršaj, opis, datum, nivo...)" label="Pretraga prekršaja"
                        ukupno={rezultat.list.filter((f) => !filterTip || f.tip === filterTip).length} pronadjeno={prikaz.length} jedinica="prekršaja" />
                    <div ref={tableRef}>
                    <DataTable value={prikaz} dataKey="key" {...PAGINATOR} rows={poStrani} first={first} onPage={(e) => setFirst(e.first)} size="small" rowGroupMode="subheader" groupRowsBy="vozac" sortMode="single" sortField="vozac" sortOrder={1}
                        rowGroupHeaderTemplate={(f) => <span className="group-head"><i className="pi pi-user" aria-hidden="true" /> {f.vozac} <span className="muted">{f.kompanija}</span></span>}
                        emptyMessage={tableEmpty(pretraga.trim() ? 'Nijedan prekršaj ne odgovara pretrazi' : 'Nema prekršaja za izabrane vozače i period', 'pi pi-check-circle')}>
                        <Column header="Vreme" style={{ minWidth: '9.5rem' }} body={(f) => fmtDateTime(f.vreme)} />
                        <Column header="Propis" style={{ minWidth: '8rem' }} body={(f) => <span className="pill neutral">{nazivRezima(f.rezim)}</span>} />
                        <Column header="Prekršaj" style={{ minWidth: '14rem' }} body={(f) => <div><b>{f.tipNaziv}</b>{f.trajanje != null && <small className="muted d-block">trajanje {fmtTrajanje(f.trajanje)}</small>}</div>} />
                        <Column header="Opis" style={{ minWidth: '22rem' }} body={(f) => <span className="fault-text">{f.opis}</span>} />
                        <Column header="Nivo" style={{ minWidth: '6.5rem' }} body={nivoBody} />
                        <Column header="Član" style={{ minWidth: '7rem' }} body={(f) => f.clanTacka || '—'} />
                        <Column header="Kazna" style={{ minWidth: '12rem' }} body={kaznaBody} />
                    </DataTable>
                    </div>
                </section>
            )}
        </BlockUI>
    );
}

/* ── Iznosi kazni ─────────────────────────────────────────────────────────── */
function Iznosi() {
    const [trazTip, setTrazTip] = useState('');
    const [trazVan, setTrazVan] = useState('');
    const tipovi = useMemo(() => filtriraj(TIPOVI, trazTip, (t) => [nazivRezima(t.rezim), t.naziv, t.osnov]), [trazTip]);
    const van = useMemo(() => filtriraj(KAZNE_VAN_OBRACUNA, trazVan, (k) => [k.opis, k.osnov]), [trazVan]);
    return (
        <div className="stack">
            <section className="panel" aria-labelledby="kz-iz">
                <div className="panel-head"><div>
                    <h3 className="panel-title" id="kz-iz">Iznosi kazni po nivou</h3>
                    <p className="panel-sub">Fiksni iznosi — Zakon o radnom vremenu posade vozila u drumskom prevozu i tahografima (Sl. glasnik RS 96/15, 95/18, 96/25), čl. 54a–61.</p>
                </div></div>
                <DataTable value={NIVOI_LISTA} dataKey="nivo" size="small">
                    <Column header="Nivo" body={(n) => <span className={`pill ${NIVO_KLASA[n.nivo]}`}>Nivo {n.nivo}</span>} />
                    <Column header="Član (pravno lice / vozač)" body={(n) => `čl. ${n.clan} / čl. ${n.clanVozac}`} />
                    <Column header="Pravno lice" headerClassName="num" bodyClassName="num" body={(n) => fmtRsd(n.pravno)} />
                    <Column header="Preduzetnik" headerClassName="num" bodyClassName="num" body={(n) => fmtRsd(n.preduzetnik)} />
                    <Column header="Odgovorno lice" headerClassName="num" bodyClassName="num" body={(n) => fmtRsd(n.odgovorno)} />
                    <Column header="Vozač" headerClassName="num" bodyClassName="num" body={(n) => fmtRsd(n.vozac)} />
                </DataTable>
            </section>
            <section className="panel" aria-labelledby="kz-tp">
                <div className="panel-head"><div>
                    <h3 className="panel-title" id="kz-tp">Prekršaji koji se obračunavaju</h3>
                    <p className="panel-sub">Nivo zavisi od veličine odstupanja (npr. dnevna vožnja: preko 15 min → nivo 4, 1h → 3, 2h → 2, 50% → 1). Odstupanje do 15 min nije kažnjivo.</p>
                </div></div>
                <TableSearch value={trazTip} onChange={setTrazTip} placeholder="Pretraga (propis, prekršaj, osnov...)" label="Pretraga prekršaja koji se obračunavaju"
                    ukupno={TIPOVI.length} pronadjeno={tipovi.length} />
                <DataTable value={tipovi} dataKey="kljuc" size="small" rowGroupMode="subheader" groupRowsBy="rezim" emptyMessage={tableEmpty('Nijedan prekršaj ne odgovara pretrazi')}
                    rowGroupHeaderTemplate={(t) => <span className="group-head">{nazivRezima(t.rezim)}</span>}>
                    <Column header="Prekršaj" field="naziv" />
                    <Column header="Osnov" field="osnov" style={{ minWidth: '14rem' }} />
                </DataTable>
            </section>
            <section className="panel" aria-labelledby="kz-van">
                <div className="panel-head"><div>
                    <h3 className="panel-title" id="kz-van">Prekršaji koji se ne vide iz podataka tahografa</h3>
                    <p className="panel-sub">Navedeni radi potpunosti — ne mogu se utvrditi iz aktivnosti vozača.</p>
                </div></div>
                <TableSearch value={trazVan} onChange={setTrazVan} placeholder="Pretraga (prekršaj, osnov, kazna...)" label="Pretraga prekršaja van obračuna"
                    ukupno={KAZNE_VAN_OBRACUNA.length} pronadjeno={van.length} />
                <DataTable value={van} dataKey="opis" size="small" emptyMessage={tableEmpty('Nijedan prekršaj ne odgovara pretrazi')}>
                    <Column header="Prekršaj" field="opis" />
                    <Column header="Osnov i kazna" field="osnov" />
                </DataTable>
            </section>
        </div>
    );
}

/* ── Podešavanja prikaza ─────────────────────────────────────────────────── */
function Podesavanja({ podesavanja, onSaved }) {
    const alerts = useAlerts();
    const [lokalno, setLokalno] = useState(podesavanja);
    const [saving, setSaving] = useState(false);
    useEffect(() => { setLokalno(podesavanja); }, [podesavanja]);

    const vidljiv = (t) => lokalno[t.kljuc] !== false;
    const promeni = (t, v) => setLokalno((x) => ({ ...x, [t.kljuc]: v }));
    const izmenjeno = TIPOVI.some((t) => (lokalno[t.kljuc] !== false) !== (podesavanja[t.kljuc] !== false));

    const sacuvaj = async () => {
        setSaving(true);
        try {
            const items = TIPOVI.map((t) => ({ regime: t.rezim, faultType: t.kljuc, visible: vidljiv(t) }));
            const saved = await kazneApi.saveSettings(items);
            onSaved(saved);
            alerts.success('Podešavanja prikaza su sačuvana');
        } catch { /* toast */ }
        setSaving(false);
    };

    return (
        <div className="stack">
            <div className="panel">
                <div className="panel-head">
                    <div>
                        <h3 className="panel-title">Prikaz tipova prekršaja</h3>
                        <p className="panel-sub">Isključen tip se ne prikazuje u obračunu ni u izvozu. Podešavanje važi za sve korisnike.</p>
                    </div>
                    <div className="page-actions">
                        <Button label="Vrati" text severity="secondary" disabled={!izmenjeno || saving} onClick={() => setLokalno(podesavanja)} />
                        <Button label="Sačuvaj" icon="pi pi-check" disabled={!izmenjeno || saving} loading={saving} onClick={sacuvaj} />
                    </div>
                </div>
            </div>
            <div className="settings-grid">
                {REZIMI.map((r) => (
                    <section key={r.kljuc} className="panel" aria-labelledby={`kz-s-${r.kljuc}`}>
                        <div className="panel-head"><div>
                            <h3 className="panel-title" id={`kz-s-${r.kljuc}`}>{r.naziv}</h3>
                            <p className="panel-sub">{r.opis}</p>
                        </div></div>
                        <div className="switch-list">
                            {TIPOVI.filter((t) => t.rezim === r.kljuc).map((t) => (
                                <label key={t.kljuc} className="switch-row" htmlFor={`kz-sw-${t.kljuc}`}>
                                    <span><span className="switch-title">{t.naziv}</span><small className="muted">{t.osnov}</small></span>
                                    <InputSwitch inputId={`kz-sw-${t.kljuc}`} checked={vidljiv(t)} onChange={(e) => promeni(t, e.value)} />
                                </label>
                            ))}
                        </div>
                    </section>
                ))}
            </div>
        </div>
    );
}

/* ── stranica ─────────────────────────────────────────────────────────────── */
export default function Kazne({ active }) {
    const [podesavanja, setPodesavanja] = useState({}); // { kljucTipa: visible }
    const toMap = (list) => Object.fromEntries((list || []).map((s) => [s.faultType, !!s.visible]));
    useEffect(() => { kazneApi.settings().then((l) => setPodesavanja(toMap(l))).catch(() => {}); }, []);
    const skriveni = useMemo(() => new Set(Object.entries(podesavanja).filter(([, v]) => v === false).map(([k]) => k)), [podesavanja]);

    return (
        <>
            <PageHeader title="Kazne" subtitle="Prekršaji iz aktivnosti vozača po AETR-u i propisima Republike Srbije, sa iznosima kazni po Zakonu." />
            <TabView className="page-tabs">
                <TabPanel header="Prekršaji" leftIcon="pi pi-exclamation-triangle"><Prekrsaji skriveni={skriveni} active={active} /></TabPanel>
                <TabPanel header="Iznosi kazni" leftIcon="pi pi-money-bill"><Iznosi /></TabPanel>
                <TabPanel header="Podešavanja" leftIcon="pi pi-sliders-h">
                    <Podesavanja podesavanja={podesavanja} onSaved={(l) => setPodesavanja(toMap(l))} />
                </TabPanel>
            </TabView>
        </>
    );
}
